import React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Label - Komponent etykiety formularza w stylu Radix Themes
 * 
 * Funkcje:
 * - Bazuje na Radix UI Label Primitives
 * - Accessibility (a11y) - poprawne powiązanie z inputem
 * - Rozmiary zgodne z 8px scale
 * - Required indicator (*)
 * 
 * @example
 * ```tsx
 * <Label htmlFor="email" required>
 *   Email
 * </Label>
 * <Input id="email" type="email" />
 * ```
 */

const labelVariants = cva(
  cn(
    "text-[var(--font-size-2)]",
    "font-[var(--font-weight-medium)]",
    "leading-none",
    "text-[var(--text-white-95)]",
    "peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
  ),
  {
    variants: {
      size: {
        sm: "text-[var(--font-size-1)]",
        base: "text-[var(--font-size-2)]",
        lg: "text-[var(--font-size-3)]",
      },
      color: {
        default: "text-[var(--text-white-95)]",
        muted: "text-[var(--text-white-80)]",
        subtle: "text-[var(--text-white-70)]",
      },
    },
    defaultVariants: {
      size: "base",
      color: "default",
    },
  }
)

export interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {
  /** Pokazuje gwiazdkę (*) dla wymaganych pól */
  required?: boolean
}

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, size, color, required, children, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      labelVariants({ size, color }),
      "mb-[var(--space-3)]",
      "inline-block",
      className
    )}
    {...props}
  >
    {children}
    {required && (
      <span 
        className="ml-1 text-[var(--danger-9)]" 
        aria-label="wymagane"
      >
        *
      </span>
    )}
  </LabelPrimitive.Root>
))
Label.displayName = LabelPrimitive.Root.displayName

export { Label, labelVariants }

